import React, { useState, useEffect, useCallback } from 'react';
import { X } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { sendMessage } from '../../firebase/conversation.service';
import { filesToValidatedFiles, MAX_ATTACHMENT_SIZE } from '../../utils/attachmentHelpers';
import { uploadAttachments } from '../../utils/uploadAttachments';
import { formatMessageTime, getMessageSourceLabel } from '../../utils/inboxHelpers';
import { getRunsForLead, stopRun } from '../../firebase/automationRuns.service';
import { sanitizeHtml, containsHtml } from '../../utils/sanitizeHtml';
import AttachmentList from './AttachmentList';
import { useEmailSignature } from '../../hooks/useEmailSignature';

const getMessageContent = (message) => {
    const candidates = [message?.content, message?.body, message?.html, message?.text];
    return candidates.find((value) => typeof value === 'string' && value.length > 0) || '';
};

const shouldRenderHtml = (message, content) => {
    return Boolean(message?.isHtml) || containsHtml(content);
};

const ChatView = ({ chat, messages = [], channel, setChannel, messageInput, setMessageInput, handleSendMessage, loading = false, onToastShow }) => {
    const { user } = useAuth();
    const emailSignature = useEmailSignature();
    const [sending, setSending] = useState(false);
    const [error, setError] = useState(null);
    const [subject, setSubject] = useState('');
    // pendingFiles are File objects kept in memory until Send is clicked
    const [pendingFiles, setPendingFiles] = useState([]);
    // Stop Bot state
    const [hasActiveAutomation, setHasActiveAutomation] = useState(false);
    const [stoppingBot, setStoppingBot] = useState(false);

    // Check for active automation runs when chat changes
    useEffect(() => {
        if (!chat?.leadId) {
            setHasActiveAutomation(false);
            return;
        }

        let cancelled = false;
        const checkAutomation = async () => {
            try {
                const runs = await getRunsForLead(chat.leadId);
                if (!cancelled) {
                    const activeRuns = runs.filter(r =>
                        ['running', 'waiting', 'paused'].includes((r.status || '').toLowerCase())
                    );
                    setHasActiveAutomation(activeRuns.length > 0);
                }
            } catch (err) {
                console.error('Error checking automation runs:', err);
            }
        };
        checkAutomation();
        return () => { cancelled = true; };
    }, [chat?.leadId]);

    // Stop Bot handler
    const handleStopBot = useCallback(async () => {
        if (!chat?.leadId || stoppingBot) return;
        setStoppingBot(true);
        try {
            const runs = await getRunsForLead(chat.leadId);
            const activeRuns = runs.filter(r =>
                ['running', 'waiting', 'paused'].includes((r.status || '').toLowerCase())
            );
            await Promise.all(activeRuns.map(run => stopRun(run.id, 'manual')));
            setHasActiveAutomation(false);
            if (onToastShow) {
                onToastShow({ type: 'success', text: 'Automation stopped successfully.' });
            }
        } catch (err) {
            console.error('Error stopping automation:', err);
            if (onToastShow) {
                onToastShow({ type: 'error', text: 'Failed to stop automation.' });
            }
        } finally {
            setStoppingBot(false);
        }
    }, [chat?.leadId, stoppingBot, onToastShow]);

    const handleSendFirebaseMessage = async () => {
        if (!messageInput.trim() && pendingFiles.length === 0) return;
        if (!chat?.id) {
            setError('Conversation not initialized');
            return;
        }

        setSending(true);
        setError(null);

        try {
            // generate messageId before any upload
            const clientMessageId = `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
            // Upload pending files first (only after user clicks Send)
            let attachmentsMeta = [];
            if (pendingFiles.length > 0) {
                const uploaded = await uploadAttachments({ files: pendingFiles, userId: user?.uid, scope: 'conversation', conversationId: chat.id, messageId: clientMessageId });
                attachmentsMeta = uploaded;
            }

            // Send message through Firebase (service will persist message and attempt delivery)
            // For EMAIL: also send an html version so the body + signature render identically
            const htmlBody = channel.toUpperCase() === 'EMAIL'
                ? messageInput.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/\n/g, '<br/>')
                : undefined;

            const result = await sendMessage(chat.id, {
                text: messageInput,
                html: htmlBody || undefined,
                channel: channel.toUpperCase(),
                isSender: true,
                subject: subject || undefined,
                attachments: attachmentsMeta.length ? attachmentsMeta : undefined,
                messageId: clientMessageId,
            });

            // Always show toast message (success or failure)
            if (result.toastMessage && onToastShow) {
                onToastShow(result.toastMessage);
            }

            // Only clear input if message was successfully created
            if (result.success) {
                setMessageInput('');
                setSubject('');
                setPendingFiles([]);
            } else {
                // Message failed to deliver - do NOT clear input so user can retry
                setError('Message failed to send');
            }
        } catch (err) {
            console.error('Error sending message:', err);
            // Show error toast
            if (onToastShow) {
                onToastShow({
                    type: 'error',
                    text: '❌ Message failed to send. Please try again.',
                });
            }
            setError('Failed to send message');
        } finally {
            setSending(false);
        }
    };

    // Handle undefined chat
    if (!chat) {
        return (
            <div className="flex flex-col h-full items-center justify-center">
                <p className="text-sm text-stone-500">Loading conversation...</p>
            </div>
        );
    }

    return (
        <div className="flex flex-col h-full">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-notion-border">
                <div>
                    <h2 className="text-lg font-semibold text-notion-text">{chat?.name || 'Loading...'}</h2>
                </div>
                <button
                    onClick={handleStopBot}
                    disabled={!hasActiveAutomation || stoppingBot}
                    className={`px-3 py-1 text-xs font-medium rounded transition ${hasActiveAutomation
                        ? 'text-white bg-red-500 hover:bg-red-600'
                        : 'text-stone-400 bg-stone-100 cursor-not-allowed'
                        } disabled:opacity-50`}
                >
                    {stoppingBot ? 'Stopping...' : 'Stop Bot'}
                </button>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto px-6 pt-6 pb-8 space-y-6">
                {loading ? (
                    <div className="flex items-center justify-center h-full">
                        <p className="text-sm text-stone-500">Loading messages...</p>
                    </div>
                ) : messages.length === 0 ? (
                    <div className="flex items-center justify-center h-full">
                        <p className="text-sm text-stone-500">No messages yet. Start the conversation!</p>
                    </div>
                ) : (
                    <>
                        {/* Conversation Started separator */}
                        <div className="flex justify-center py-2">
                            <span className="px-4 py-1 text-xs text-stone-500 bg-stone-100 rounded-full">
                                Conversation Started
                            </span>
                        </div>

                        {messages.map((message, index) => {
                            // Determine ownership: agent/system → right, lead → left
                            // Primary: isSender flag. Fallback: source field for older messages.
                            const isOutgoing = message.isSender === true
                                || message.source === 'manual'
                                || message.source === 'broadcast'
                                || message.source === 'automation'
                                || message.isSystem === true;
                            const messageContent = getMessageContent(message);
                            const renderHtml = shouldRenderHtml(message, messageContent);

                            // Broadcast messages use the same card as automated system messages
                            const isBroadcast = message.isBroadcast === true || message.source === 'broadcast';

                            /* ---------- Automated System card (right-aligned) ---------- */
                            if (message.isSystem || isBroadcast) {
                                return (
                                    <div key={index} className="flex justify-end">
                                        <div className="flex flex-col items-end" style={{ width: '68%' }}>
                                            <div className="w-full rounded-xl bg-stone-50 border border-stone-200 text-stone-700 overflow-hidden">
                                                {/* Label row inside bubble */}
                                                <div className="flex items-center gap-1.5 px-5 pt-3 pb-2">
                                                    <svg className="w-3.5 h-3.5 text-stone-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></svg>
                                                    <span className="text-[11px] font-semibold tracking-wider uppercase text-stone-400">{isBroadcast ? 'BROADCAST' : 'AUTOMATED SYSTEM'}</span>
                                                </div>
                                                {/* Divider */}
                                                <div className="border-t border-stone-200 mx-5" />
                                                {/* Message content */}
                                                <div className="px-5 pt-3 pb-4">
                                                    {message.channel === 'EMAIL' && (message.metadata?.subject || message.subject) && (
                                                        <>
                                                            <p className="text-sm font-bold mb-2">{message.metadata?.subject || message.subject}</p>
                                                            {renderHtml ? (
                                                                <div className="text-sm email-html-body break-words" dangerouslySetInnerHTML={{ __html: sanitizeHtml(messageContent) }} />
                                                            ) : (
                                                                <p className="text-sm leading-relaxed whitespace-pre-wrap break-words">{messageContent}</p>
                                                            )}
                                                        </>
                                                    )}
                                                    {(message.channel !== 'EMAIL' || !(message.metadata?.subject || message.subject)) && (
                                                        renderHtml ? (
                                                            <div className="text-sm email-html-body break-words" dangerouslySetInnerHTML={{ __html: sanitizeHtml(messageContent) }} />
                                                        ) : (
                                                            <p className="text-sm leading-relaxed whitespace-pre-wrap break-words">{messageContent}</p>
                                                        )
                                                    )}
                                                    <AttachmentList attachments={message.attachments} messageId={message.id} isSent={message.isSender} />
                                                </div>
                                            </div>
                                            <p className="text-[10px] text-stone-400 mt-1.5">
                                                {formatMessageTime(message.timestamp)}
                                                {message.channel && ` • via ${message.channel}`}
                                            </p>
                                        </div>
                                    </div>
                                );
                            }

                            /* ---------- Agent (right) / Lead (left) bubbles ---------- */
                            return (
                                <div
                                    key={index}
                                    className={`flex ${isOutgoing ? 'justify-end' : 'justify-start'}`}
                                >
                                    <div
                                        className={`flex flex-col ${isOutgoing ? 'items-end' : 'items-start'}`}
                                        style={{ maxWidth: '58%' }}
                                    >
                                        {getMessageSourceLabel(message) && (
                                            <span className="text-[10px] font-medium text-blue-500 mb-1">{getMessageSourceLabel(message)}</span>
                                        )}
                                        <div
                                            className={`px-4 py-3 rounded-2xl ${isOutgoing
                                                ? 'bg-stone-700 text-white'
                                                : 'bg-white border border-stone-200 text-notion-text'
                                                }`}
                                        >
                                            {message.channel === 'EMAIL' && (message.metadata?.subject || message.subject) && (
                                                <>
                                                    <p className="text-sm font-bold mb-2">{message.metadata?.subject || message.subject}</p>
                                                    {renderHtml ? (
                                                        <div className="text-sm email-html-body break-words" dangerouslySetInnerHTML={{ __html: sanitizeHtml(messageContent) }} />
                                                    ) : (
                                                        <p className="text-sm leading-relaxed whitespace-pre-wrap break-words">{messageContent}</p>
                                                    )}
                                                </>
                                            )}
                                            {(message.channel !== 'EMAIL' || !(message.metadata?.subject || message.subject)) && (
                                                renderHtml ? (
                                                    <div className="text-sm email-html-body break-words" dangerouslySetInnerHTML={{ __html: sanitizeHtml(messageContent) }} />
                                                ) : (
                                                    <p className="text-sm leading-relaxed whitespace-pre-wrap break-words">{messageContent}</p>
                                                )
                                            )}
                                            <AttachmentList attachments={message.attachments} messageId={message.id} isSent={message.isSender} />
                                        </div>
                                        <p className="text-[10px] text-stone-400 mt-1.5">
                                            {formatMessageTime(message.timestamp)}
                                            {message.channel && ` • via ${message.channel}`}
                                        </p>
                                    </div>
                                </div>
                            );
                        })}
                    </>
                )}
            </div>

            {/* Input */}
            <div className="p-4 border-t border-notion-border">
                {error && <p className="text-xs text-red-500 mb-2">{error}</p>}
                <div className="flex items-center gap-2 mb-2">
                    {['SMS', 'Email'].map((chan) => (
                        <button
                            key={chan}
                            disabled={sending}
                            className={`px-3 py-1 text-xs font-medium rounded transition ${channel === chan.toLowerCase()
                                ? 'bg-stone-800 text-white'
                                : 'bg-stone-100 text-stone-800 hover:bg-stone-200'
                                } disabled:opacity-50`}
                            onClick={() => setChannel(chan.toLowerCase())}
                        >
                            {chan}
                        </button>
                    ))}
                </div>

                {/* Subject input - EMAIL ONLY (SMS has no subject) */}
                {channel === 'email' && (
                    <div className="mb-2">
                        <label className="text-xs text-notion-text-light">Subject</label>
                        <input
                            type="text"
                            placeholder="Enter email subject"
                            value={subject}
                            onChange={(e) => setSubject(e.target.value)}
                            disabled={sending}
                            className="w-full mt-1 px-3 py-2 text-sm border border-notion-border rounded focus:outline-none focus:ring-1 focus:ring-stone-800 disabled:bg-stone-50"
                        />
                    </div>
                )}

                <div className="flex items-center gap-2">
                    <input
                        type="text"
                        placeholder="Type a message..."
                        value={messageInput}
                        onChange={(e) => setMessageInput(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && !sending && handleSendFirebaseMessage()}
                        disabled={sending}
                        className="flex-1 px-3 py-2 text-sm border border-notion-border rounded focus:outline-none focus:ring-1 focus:ring-stone-800 disabled:bg-stone-50"
                    />

                    <div className="flex items-center gap-2">
                        {/* Attach button - EMAIL ONLY (SMS has no attachments) */}
                        {channel === 'email' && (
                            <label className="px-3 py-2 bg-stone-100 text-stone-800 rounded cursor-pointer text-sm">
                                Attach
                                <input
                                    type="file"
                                    multiple
                                    accept=".pdf,image/*,.doc,.docx,.txt"
                                    className="hidden"
                                    disabled={sending}
                                    onChange={async (e) => {
                                        try {
                                            const files = e.target.files;
                                            if (!files || files.length === 0) return;
                                            try {
                                                const validFiles = filesToValidatedFiles(files);
                                                setPendingFiles(prev => [...prev, ...validFiles]);
                                            } catch (err) {
                                                console.error('Attachment error:', err);
                                                setError(err.message || 'Attachment error');
                                            }
                                        } catch (err) {
                                            console.error('Attachment error:', err);
                                            setError(err.message || 'Attachment error');
                                        } finally {
                                            e.target.value = null;
                                        }
                                    }}
                                />
                            </label>
                        )}
                        <button
                            onClick={handleSendFirebaseMessage}
                            disabled={sending || (!messageInput.trim() && pendingFiles.length === 0)}
                            className="px-3 py-2 text-sm font-medium text-white bg-stone-800 rounded hover:bg-stone-700 transition disabled:bg-stone-400"
                        >
                            {sending ? `Sending ${channel.toUpperCase()}...` : `Send ${channel.toUpperCase()}`}
                        </button>
                    </div>
                </div>

                {pendingFiles.length > 0 && (
                    <div className="mt-2 space-y-1">
                        {pendingFiles.map((a, i) => (
                            <div key={i} className="flex items-center justify-between text-sm bg-stone-50 p-2 rounded">
                                <span className="truncate">{a.name} ({Math.round(a.size / 1024)} KB)</span>
                                <button disabled={sending} onClick={() => setPendingFiles(prev => prev.filter((_, idx) => idx !== i))} className="text-xs text-red-500 ml-2">Remove</button>
                            </div>
                        ))}
                    </div>
                )}

                {/* Email signature preview */}
                {channel === 'email' && emailSignature && (
                    <div className="mt-2 border-t border-dashed border-stone-200 pt-2">
                        <p className="text-[10px] text-stone-400 uppercase tracking-wider mb-1">Signature</p>
                        <div
                            className="text-xs text-stone-500 leading-relaxed"
                            dangerouslySetInnerHTML={{ __html: emailSignature }}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

export default ChatView;